import { useAtlassianAccountTraits } from '@confluence/targeting/entry-points/useAtlassianAccountTraits.ts';

/**
 * Name of the TAP trait that contains the up-to-3 AAIDs of the user's
 * top Rovo chat collaborators. The trait is registered externally in TAP;
 * Confluence's `useAtlassianAccountTraits` hook fetches all enrolled traits on
 * page load (with SSR preload) and we simply look this one up by name.
 *
 * The trait value is a comma-separated list of AAIDs, e.g.
 *   "712020:abc...,712020:def...,712020:ghi..."
 */
export const FOMO_NUDGE_TRAIT_NAME = 'rovo_chat_top_collaborators_all_users';

/**
 * Name of the TAP trait that marks the user as an active Rovo Chat MAU.
 * When `true`, the FOMO nudge should not show — these users already use Rovo
 * regularly, so the "your teammates are using Rovo" framing is not relevant.
 */
export const IS_ROVO_CHAT_MAU_TRAIT = 'is_rovo_chat_mau';

const MAX_AAIDS = 3;

/**
 * Extract up to 3 Atlassian Account IDs from the FOMO nudge TAP trait.
 *
 * The `enabled` flag should reflect "this user is in treatment AND the
 * killswitch is on" — when `false`, the hook skips bootstrapping the
 * traits fetch entirely (no HTTP request) and returns
 * `{ aaids: [], isReady: true }` immediately.
 *
 * Returns `{ aaids, isReady }`:
 *   - while traits are loading: `{ aaids: [], isReady: false }` — nudge should hide
 *   - on error / missing trait / empty trait value: `{ aaids: [], isReady: true }` —
 *     the consumer can use this signal to fire a "no eligible collaborators" analytic
 *   - trait present with non-empty value: `{ aaids: [...], isReady: true }`
 *
 * The hook is intentionally side-effect free (no analytics, no logging). Any
 * analytic firing should happen at the consumer level so that gating
 * (killswitch / experiment) can be applied first.
 */
export function useFomoNudgeTrait({ enabled }: { enabled: boolean }): {
	aaids: string[];
	isReady: boolean;
	/** Upstream fetch landed (data delivered or SSR preload available). False for !enabled / loading / pre-fetch. */
	loaded: boolean;
	/**
	 * Whether the user is an active Rovo Chat MAU. `null` while loading.
	 * Defaults to `false` when the trait is missing — graceful degradation:
	 * if the trait isn't enrolled for this user, treat them as a non-MAU and
	 * let other gates decide whether to show the nudge.
	 */
	isRovoChatMau: boolean | null;
} {
	const { data, loading, error } = useAtlassianAccountTraits({ skipBootstrap: !enabled });

	if (!enabled) {
		return { aaids: [], isReady: true, loaded: false, isRovoChatMau: null };
	}

	if (loading) {
		return { aaids: [], isReady: false, loaded: false, isRovoChatMau: null };
	}

	if (error) {
		return { aaids: [], isReady: true, loaded: false, isRovoChatMau: null };
	}

	if (data == null) {
		return { aaids: [], isReady: false, loaded: false, isRovoChatMau: null };
	}

	const traitValue =
		data?.attributes?.find((attr) => attr.name === FOMO_NUDGE_TRAIT_NAME)?.value ?? '';

	// `value` is typed as `string` by the trait resource, but in practice Site
	// User Traits can deliver booleans/numbers (or `null`) for this attribute
	// depending on the upstream pipeline. Coerce defensively so a non-string
	// shape cannot crash the hook (and therefore the entire page tree above it).
	const rawMauValue = data?.attributes?.find((attr) => attr.name === IS_ROVO_CHAT_MAU_TRAIT)?.value;
	const isRovoChatMau = rawMauValue != null && String(rawMauValue).toLowerCase() === 'true';

	const aaids: string[] = [];
	const atlassianIds = traitValue.split(',').slice(0, MAX_AAIDS);
	atlassianIds.forEach((atlassianId) => {
		const trimmed = atlassianId.trim();
		if (trimmed) aaids.push(trimmed);
	});

	return { aaids, isReady: true, loaded: true, isRovoChatMau };
}
